import React, { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { MobileSectionHeader, FeaturedEventCard, QuickActionCard, MobileEventCard, MobileEmptyState, MobileLoadingSkeleton, PullToRefresh } from './mobile';

const parseLocalDate = (dateStr) => {
  if (!dateStr) return null;
  if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  return new Date(dateStr);
};

const formatRange = (start, end) => {
  const s = parseLocalDate(start);
  if (!s) return 'Date TBD';
  const e = end ? parseLocalDate(end) : null;
  const opts = { month: 'short', day: 'numeric' };
  if (!e || s.toDateString() === e.toDateString()) {
    return s.toLocaleDateString('en-US', { ...opts, year: 'numeric' });
  }
  if (s.getMonth() === e.getMonth()) {
    return `${s.toLocaleDateString('en-US', opts)} – ${e.getDate()}, ${e.getFullYear()}`;
  }
  return `${s.toLocaleDateString('en-US', opts)} – ${e.toLocaleDateString('en-US', { ...opts, year: 'numeric' })}`;
};

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
};

const QUICK_ACTIONS = [
  {
    label: 'Hotels',
    to: '/travel',
    icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>,
    description: 'Book your stay',
  },
  {
    label: 'Flights',
    to: '/travel',
    icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>,
    description: 'Find flights',
  },
  {
    label: 'Schedule',
    to: '/schedule',
    icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
    description: 'View itinerary',
  },
  {
    label: 'Speakers',
    to: '/speakers',
    icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>,
    description: 'Meet the team',
  },
  {
    label: 'Venue',
    to: '/venue',
    icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
    description: 'Event location',
  },
  {
    label: 'Transport',
    to: '/travel',
    icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>,
    description: 'Get around',
  },
];

const MobileHome = ({ events = [], savedEvents = [], onSaveToggle }) => {
  const { user, profile } = useAuth();
  const [loading, setLoading] = useState(events.length === 0);

  useEffect(() => {
    if (events.length > 0) setLoading(false);
  }, [events]);

  const upcoming = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return [...events]
      .filter((e) => {
        const d = parseLocalDate(e.end || e.start || e.date);
        return d && d >= today && e.status !== 'cancelled';
      })
      .sort((a, b) => parseLocalDate(a.start || a.date) - parseLocalDate(b.start || b.date));
  }, [events]);

  const next = upcoming[0];
  const trending = useMemo(() => upcoming.slice(0, 6), [upcoming]);
  const recommended = useMemo(() => {
    const savedSet = new Set(savedEvents);
    return upcoming.filter((e) => !savedSet.has(e.id)).slice(0, 4);
  }, [upcoming, savedEvents]);

  const saved = useMemo(
    () => events.filter((e) => savedEvents.includes(e.id)),
    [events, savedEvents]
  );

  const name =
    profile?.full_name?.split(' ')[0] ||
    user?.email?.split('@')[0] ||
    'there';

  const handleSave = (eventId) => {
    if (onSaveToggle) onSaveToggle(eventId);
  };

  const handleRefresh = async () => {
    window.location.reload();
  };

  return (
    <PullToRefresh onRefresh={handleRefresh}>
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pb-4">
      {/* Header with gradient */}
      <header
        className="px-5 pb-5 bg-gradient-to-br from-primary-dark to-primary text-white rounded-b-3xl shadow-lg"
        style={{ paddingTop: 'calc(env(safe-area-inset-top) + 1.25rem)' }}
      >
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <p className="text-white/70 text-sm">{greeting()},</p>
            <h1 className="font-display text-2xl font-bold capitalize truncate">{name}</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              className="w-10 h-10 rounded-full bg-white/15 backdrop-blur flex items-center justify-center ring-1 ring-white/20 active:scale-95 transition-transform"
              aria-label="Notifications"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
            </button>
            <Link
              to={user ? '/my-profile' : '/login'}
              className="w-11 h-11 rounded-full bg-white/15 backdrop-blur flex items-center justify-center text-lg font-semibold ring-1 ring-white/20 active:scale-95 transition-transform"
              aria-label="Profile"
            >
              {(user?.email?.[0] || 'A').toUpperCase()}
            </Link>
          </div>
        </div>

        {/* Up Next hero card */}
        {loading ? (
          <div className="mt-5">
            <MobileLoadingSkeleton variant="hero" />
          </div>
        ) : next ? (
          <div className="mt-5">
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-secondary">
                <span className="w-2 h-2 rounded-full bg-secondary animate-pulse" />
                UP NEXT
              </span>
            </div>
            <FeaturedEventCard
              event={next}
              isSaved={savedEvents.includes(next.id)}
              onSaveToggle={handleSave}
            />
          </div>
        ) : (
          <div className="mt-5 bg-white/10 backdrop-blur rounded-2xl p-5 text-center">
            <p className="text-white/90 text-sm mb-3">No upcoming conferences yet.</p>
            <Link
              to="/events"
              className="inline-flex items-center px-5 py-2.5 bg-white text-primary text-sm font-semibold rounded-xl active:scale-95 transition-transform"
            >
              Discover Events
            </Link>
          </div>
        )}
      </header>

      {/* Continue Planning */}
      <section className="mt-6">
        <MobileSectionHeader title="Continue Planning" />
        <div className="grid grid-cols-3 gap-3 px-4">
          {QUICK_ACTIONS.map((a) => (
            <QuickActionCard
              key={a.label}
              to={a.to}
              icon={a.icon}
              label={a.label}
              description={a.description}
            />
          ))}
        </div>
      </section>

      {/* Trending Conferences */}
      {loading ? (
        <section className="mt-6">
          <MobileSectionHeader title="Trending Conferences" />
          <div className="px-4 space-y-3">
            <MobileLoadingSkeleton variant="card" count={2} />
          </div>
        </section>
      ) : trending.length > 0 ? (
        <section className="mt-6">
          <MobileSectionHeader
            title="Trending Conferences"
            action={<Link to="/events" className="text-sm font-medium text-primary hover:text-primary-light transition-colors">See all</Link>}
          />
          <div className="flex gap-3 overflow-x-auto px-4 pb-2 snap-x snap-mandatory scrollbar-hide">
            {trending.map((e) => (
              <div key={e.id} className="snap-start shrink-0 w-72">
                <MobileEventCard
                  event={e}
                  onSaveToggle={handleSave}
                  isSaved={savedEvents.includes(e.id)}
                />
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {/* Recommended for You */}
      {!loading && recommended.length > 0 && (
        <section className="mt-6">
          <MobileSectionHeader title="Recommended for You" />
          <div className="px-4 space-y-3">
            {recommended.map((e) => (
              <MobileEventCard
                key={e.id}
                event={e}
                onSaveToggle={handleSave}
                isSaved={savedEvents.includes(e.id)}
              />
            ))}
          </div>
        </section>
      )}

      {/* My Plan */}
      {!loading && saved.length > 0 && (
        <section className="mt-6">
          <MobileSectionHeader
            title="My Plan"
            action={<span className="text-sm text-gray-400">{saved.length} saved</span>}
          />
          <div className="px-4 space-y-2">
            {saved.slice(0, 5).map((e) => (
              <Link
                key={e.id}
                to={`/events/${e.id}`}
                className="flex items-center gap-3 bg-white dark:bg-gray-800 rounded-2xl p-3 shadow-card active:scale-[0.98] transition-transform"
              >
                <div className="w-11 h-11 rounded-xl bg-secondary/10 text-secondary flex flex-col items-center justify-center shrink-0">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M5 4a2 2 0 012-2h6a2 2 0 012 2v14l-5-2.5L5 18V4z" />
                  </svg>
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="font-semibold text-sm text-gray-900 dark:text-white truncate">
                    {e.event || e.title}
                  </h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {formatRange(e.start || e.date, e.end)}
                  </p>
                </div>
                <svg className="w-4 h-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Empty state */}
      {!loading && events.length === 0 && (
        <div className="mt-8">
          <MobileEmptyState
            icon={<svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>}
            title="No events available"
            message="Check back soon for upcoming conferences and events."
            actionLabel="Browse Events"
            onAction={() => window.location.href = '/events'}
          />
        </div>
      )}
    </div>
    </PullToRefresh>
  );
};

export default MobileHome;
